/**
 * Visitor Intelligence client: first-party activity + identity capture.
 * Only stores contact details the visitor submits themselves.
 */
const URL_ = `https://${import.meta.env.VITE_SUPABASE_PROJECT_ID}.supabase.co/functions/v1/vi-track`;
const KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

const vid = () => {
  let v = localStorage.getItem('obc_visitor_id');
  if (!v) { v = `v_${crypto.randomUUID()}`; localStorage.setItem('obc_visitor_id', v); }
  return v;
};
const sid = () => {
  let s = sessionStorage.getItem('obc_session_id');
  if (!s) { s = `s_${crypto.randomUUID()}`; sessionStorage.setItem('obc_session_id', s); }
  return s;
};

const isPreview = () => /lovable\.(app|dev)|localhost|lovableproject/.test(location.hostname) &&
  !/obriencontractusa\.lovable\.app/.test(location.hostname);

const attribution = () => {
  const p = new URLSearchParams(location.search);
  const keys = ['utm_source','utm_medium','utm_campaign','utm_content','utm_term','gclid','gbraid','wbraid','fbclid'];
  const out: Record<string, string> = {};
  for (const k of keys) { const v = p.get(k); if (v) out[k] = v.slice(0, 300); }
  return out;
};

function send(event_type: string, meta: Record<string, unknown> = {}, identity?: Record<string, unknown>) {
  if (isPreview()) return;
  try {
    const body = JSON.stringify({
      visitor_id: vid(), session_id: sid(), event_type,
      page_path: location.pathname, page_url: location.href.slice(0, 1000),
      referrer: document.referrer || null, attribution: attribution(), meta, identity,
    });
    fetch(URL_, { method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json', apikey: KEY }, body }).catch(() => {});
  } catch { /* never break the site */ }
}

export const viPageView = () => send('page_view');
export const viEvent = (type: string, meta?: Record<string, unknown>) => send(type, meta);

/** Call when a visitor submits their own details. */
export function viIdentify(d: { name?: string | null; email?: string | null; phone?: string | null; address?: string | null; city?: string | null; state?: string | null; zip?: string | null; service_interest?: string | null; form_type?: string | null }) {
  send('form_submit', { form_type: d.form_type, service_interest: d.service_interest }, { ...d, source: 'FORM_SUBMISSION' });
}

let installed = false;
export function installVisitorIntel() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  const started = new WeakSet<HTMLFormElement>();
  const t0 = Date.now();

  document.addEventListener('click', (e) => {
    const el = (e.target as HTMLElement)?.closest('a,button') as HTMLAnchorElement | HTMLButtonElement | null;
    if (!el) return;
    const href = (el as HTMLAnchorElement).getAttribute?.('href') || '';
    const text = (el.textContent || '').trim().slice(0, 80);
    if (href.startsWith('tel:')) return send('phone_click', { text });
    if (href.startsWith('mailto:')) return send('email_click', { text });
    if (/financ/i.test(href + text)) return send('financing_click', { text, href });
    if (/visualiz/i.test(href + text)) return send('visualizer_use', { text });
    if (/estimate|inspection|quote|call|schedule|get help|request/i.test(text)) send('cta_click', { text, href });
  }, true);

  document.addEventListener('focusin', (e) => {
    const f = (e.target as HTMLElement)?.closest?.('form') as HTMLFormElement | null;
    if (f && !started.has(f)) { started.add(f); send('form_start', { form: f.getAttribute('name') || f.id || null }); }
  });

  window.addEventListener('pagehide', () => {
    send('session_end', { seconds: Math.round((Date.now() - t0) / 1000) });
  });
}
